package com.eladfinish.bible.contest.screens.search.usecases.date

import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.extensions.formatWithCommas
import com.eladfinish.bible.contest.network.ApiResult
import com.eladfinish.bible.contest.screens.search.data.SearchRepository
import com.eladfinish.bible.contest.screens.search.usecases.root.FindWordRangesInVerseUseCase
import com.eladfinish.bible.contest.screens.search.usecases.root.RootSearchVerseProvider
import com.eladfinish.bible.contest.screens.search.usecases.root.SearchRootUiResult
import com.eladfinish.bible.contest.search_summary_format
import dev.icerock.moko.resources.desc.StringDesc
import dev.icerock.moko.resources.desc.desc
import dev.icerock.moko.resources.desc.plus
import dev.icerock.moko.resources.format
import kotlinx.collections.immutable.toImmutableList

/**
 * Verses with a given date (elfifo4/dates-in-bible), highlighted like root search results.
 *
 * Always shown with only the qere: the data's word indexes ("w") count the qere text, and with the
 * ketiv shown the highlight would drift (Esther 9:15, 9:18, 9:19).
 */
class SearchDateUseCase(
    private val searchRepository: SearchRepository,
    private val rootSearchVerseProvider: RootSearchVerseProvider,
    private val findWordRangesInVerseUseCase: FindWordRangesInVerseUseCase,
) {

    suspend operator fun invoke(
        dateQuery: DateQuery,
        onError: (Pair<StringDesc, Throwable>) -> Unit,
    ): Result<SearchRootUiResult> {

        return when (val result = searchRepository.getDateResults(month = dateQuery.month, day = dateQuery.day)) {

            is ApiResult.Success -> {
                val data = result.data
                val wholeMonth = dateQuery.day == null

                // A month's list is in Bible order; show it day by day (stable, so Bible order within a day)
                val references = if (wholeMonth) data.verses.sortedBy { it.day ?: 0 } else data.verses

                rootSearchVerseProvider.prefetch(references.map { it.book to it.chapter })

                val uiResults = references.mapIndexedNotNull { index, ref ->
                    val verse = rootSearchVerseProvider(
                        bookIndex = ref.book,
                        chapterIndex = ref.chapter,
                        verseIndex = ref.verse,
                        onlyQri = true,
                    ) ?: return@mapIndexedNotNull null

                    val ranges = findWordRangesInVerseUseCase(
                        verse = verse.text,
                        wordIndexes = ref.wordIndexes,
                    )

                    val day = ref.day ?: dateQuery.day
                    val source = if (wholeMonth && day != null) {
                        "${verse.source} · ${DateQuery.dayInMonthLabel(month = dateQuery.month, day = day)}"
                    } else {
                        verse.source
                    }

                    verse.copy(
                        // the same verse can have two dates in one month (Esther 9:18)
                        id = "date_${dateQuery.month}_${day}_${ref.book}_${ref.chapter}_${ref.verse}_$index",
                        source = source,
                        highlightedRanges = ranges.toImmutableList(),
                    )
                }

                Result.success(
                    SearchRootUiResult(
                        results = uiResults.toImmutableList(),
                        report = "${dateQuery.label} · ".desc() + SharedRes.strings.search_summary_format.format(
                            data.total.formatWithCommas(),
                            data.diffVerses.formatWithCommas(),
                        ),
                    ),
                )
            }

            is ApiResult.ClientError -> {
                if (result.errorCode == NOT_FOUND) {
                    // no file for this day / month: no verse has this date
                    Result.failure(DateNotFoundException(dateQuery))
                } else {
                    onError(SERVER_ERROR.desc() to result.throwable)
                    Result.failure(Exception(SERVER_ERROR, result.throwable))
                }
            }

            is ApiResult.Failure -> {
                onError(NETWORK_ERROR.desc() to result.cause)
                Result.failure(Exception(NETWORK_ERROR, result.cause))
            }
        }
    }

    class DateNotFoundException(dateQuery: DateQuery) :
        Exception("לא נמצאו פסוקים עם התאריך ${dateQuery.label}")

    private companion object {
        const val NOT_FOUND = 404
        const val SERVER_ERROR = "שגיאה בשרת"
        const val NETWORK_ERROR = "שגיאת רשת"
    }
}
